import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  ButtonContainer,
  ButtonsBloc,
  DataTable,
  FGCustomPanel,
  FGMaskInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
  showError,
  useGridState,
  useSearchApi
} from "nsitools-react";
import * as React from "react";
import { useHistory, useLocation } from "react-router";

import {
  TuteurApi,
  TuteurLieuDoublonGridDto,
  TuteurLieuDoublonSearchDto,
  TuteurLieuDoublonSearchDtoFromJSON
} from "../../../../../api";
import { ERoutes } from "../../../../../AppRouter";
import {
  AddButton,
  CancelButton,
  EditButton,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  SmallFormGenerator,
  UnordoredListColumn,
  ViewButton
} from "../../../../../components";
import { useDialog } from "../../../../../contexts";
import { useBeIdCardContext } from "../../../../../contexts/BeIdCardContext";
import { useApiService, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface ITuteurDetailDoublonsProps {
  setBaseData: (sObj: TuteurLieuDoublonSearchDto) => void;
}

export const TuteurDetailDoublons: React.FunctionComponent<ITuteurDetailDoublonsProps> = ({ setBaseData }) => {
  const { t } = useTl();
  const dialogContext = useDialog();
  const history = useHistory();
  const { search: searchQ } = useLocation();
  const [nationalites, nLoading] = useReferential(a => a.referentialGetAllNationalite(), false, []);
  const api = useApiService(TuteurApi);
  const { foundRowsData } = useBeIdCardContext();
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [10],
    pageSize: 10,
    sortKeys: { nom: "ASC" }
  });
  const { totalCount } = tableState;

  const searchFunction = React.useCallback(
    (sObj?: TuteurLieuDoublonSearchDto) => {
      return api.tuteurSearchDoublonsLieu({ TuteurLieuDoublonSearchDto: sObj });
    },
    [api]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: false
  });
  const add = React.useCallback(() => {
    if (totalCount && totalCount > 0) {
      dialogContext.showDialog({
        message: t(ETLCodes.AddConfirmMessage),
        onConfirmed: async () => {
          history.push(`${ERoutes.tuteur}/-1/detail/edit${searchQ}`);
        }
      });
    } else {
      history.push(`${ERoutes.tuteur}/-1/detail/edit${searchQ}`);
    }
  }, [dialogContext, history, searchQ, t, totalCount]);

  const initialData = React.useMemo(() => {
    const onlyRegNatNb =
      !foundRowsData ||
      (!foundRowsData.nom && !foundRowsData.prenom && !foundRowsData.dateNaissance && foundRowsData.nationalNb);
    return TuteurLieuDoublonSearchDtoFromJSON({
      nom: foundRowsData?.nom,
      prenom: foundRowsData?.prenom,
      registreNational: onlyRegNatNb ? foundRowsData?.nationalNb : undefined,
      dateNaissance: foundRowsData?.dateNaissance
    });
  }, [foundRowsData]);

  React.useEffect(() => {
    if (foundRowsData) {
      search(initialData);
    }
  }, [foundRowsData, initialData, search]);

  const columns = React.useMemo(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: TuteurLieuDoublonGridDto) => (
          <ButtonContainer>
            {row.idtuteur ? (
              <>
                <ViewButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.tuteur}/${row.idtuteur}/detail/view`)}
                />
                <EditButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.tuteur}/${row.idtuteur}/detail/edit`)}
                />
              </>
            ) : (
              <AddButton
                tooltipText={t(ETLCodes.CreerAPartirdePersonne)}
                onClick={() => {
                  setBaseData({ ...row });
                  add();
                }}
              />
            )}
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom"
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom"
      },
      {
        header: () => t(ETLCodes.DateNaissance),
        fieldName: "dateNaissance"
      },
      {
        header: () => t(ETLCodes.NumeroNational),
        fieldName: "registreNational"
      },
      {
        header: () => t(ETLCodes.Adresse),
        fieldName: "adresse"
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      },
      {
        header: () => t(ETLCodes.Nationalite),
        fieldName: "nationalite"
      }
      // {
      //   header: () => t(ETLCodes.Forme),
      //   fieldName: "forme",
      //   render: (row: TuteurLieuDoublonGridDto) => (
      //     <UnordoredListColumn
      //       values={[
      //         { value: row.isApprenant, label: t(ETLCodes.Apprenant) },
      //         { value: row.isFormateur, label: t(ETLCodes.Formateur) },
      //         { value: row.isRepresentant, label: t(ETLCodes.Representant) },
      //         { value: row.isTuteur, label: t(ETLCodes.Tuteur) },
      //         { value: row.isChefEntreprise, label: t(ETLCodes.ChefEntreprise) },
      //         { value: row.isCollaborateur, label: t(ETLCodes.Collaborateur) },
      //         { value: row.isDelegueTutelle, label: t(ETLCodes.Referent) },
      //         { value: row.isConseillePedagogique, label: t(ETLCodes.ConseillerPedagogique) }
      //       ]}
      //     />
      //   )
      // }
    ],
    [add, history, setBaseData, t]
  );

  const [firstSearch, setFirstSearch] = React.useState(false);

  return (
    <FieldSet collapsable={false} title={t(ETLCodes.Tuteurs)}>
      <SmallFormGenerator
        enableDirtyCheck={false}
        initialValues={initialData}
        onSubmit={c => {
          if ((c.nom && c.prenom) || c.registreNational) {
            search(c);
            setFirstSearch(true);
          } else {
            showError(t(ETLCodes.NoCriteriaFilled));
          }
        }}
        editMode
        hideButtons={true}
        onCancel={() => history.push(`${ERoutes.tuteur}`)}
      >
        <FieldGroup columns={2}>
          <FGTextInput name="nom" label={t(ETLCodes.Nom)} maxLength={100} forceCase="upper" />
          <FGWalterSelectInput
            name="nationalite"
            label={t(ETLCodes.Nationalite)}
            items={nationalites}
            loading={nLoading}
          />
          <FGTextInput name="prenom" label={t(ETLCodes.Prenom)} maxLength={100} />
          <FGMaskInput
            name="registreNational"
            label={t(ETLCodes.NumeroNational)}
            cleaveOptions={{ delimiters: [".", ".", "-", "."], blocks: [2, 2, 2, 3, 2], numericOnly: true }}
          />
          <FGWalterDateMaskInput name="dateNaissance" label={t(ETLCodes.DateNaissance)} />
        </FieldGroup>
        <FieldGroup>
          <FGCustomPanel>
            {ctx => (
              <InlineButtonContainer>
                <ButtonsBloc></ButtonsBloc>
                <ButtonsBloc>
                  <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.tuteur}`)} />
                  <Button icon={IconNames.SEARCH} type="submit" text={t(ETLCodes.Search)} intent={Intent.PRIMARY} />
                  <AddButton
                    onClick={() => {
                      setBaseData(ctx.formik.values);
                      add();
                    }}
                    text={t(ETLCodes.General_Add)}
                    intent={Intent.PRIMARY}
                    minimal={false}
                    disabled={!firstSearch}
                  />
                </ButtonsBloc>
              </InlineButtonContainer>
            )}
          </FGCustomPanel>
        </FieldGroup>
        <FGCustomPanel>
          {ctx => (
            <FieldSet title={t(ETLCodes.TableResults, { count: totalCount })}>
              <DataTable dateFormat="dd-MM-yyyy" tableState={tableState} loading={loading} columns={columns} />
            </FieldSet>
          )}
        </FGCustomPanel>
      </SmallFormGenerator>
    </FieldSet>
  );
};
